import type { PermissionConstantDTO } from "./roles-permissions.schemas";

export const DOMAINS = {
	ACQUISITION_REFERRAL: "acquisitionReferral",
	CAPEX: "capex",
	TEAM_CALLINS: "teamCallins",
	QR_CODES: "qrCodes",
	GALLERIES: "galleries",
	GALLERY_FOLDERS: "galleryFolders",
	TEAM_MEMBERS: "teamMembers",
	PENDING_TEAM_MEMBERS: "pendingTeamMembers",
	SYSTEM: "system",
	INVESTMENT_LISTINGS: "investmentListings",
	NOTES: "notes",
	DEBT: "debt",
	PARTIES: "parties",
	COMPANIES: "companies",
	COMPANY_STRUCTURE: "companyStructure",
	CONTACTS: "contacts",
	REAL_ESTATE_ASSETS: "realEstateAssets",
	INVESTMENTS: "investments",
	OFFERINGS: "offerings",
	TEAM_CHARTS: "teamCharts",
	MLS_SUPRA_EVENTS: "mlsSupraEvents",
	TEAM_SHOUTOUTS: "teamShoutouts",
	DEVICES: "devices",
	JOB_JACKETS: "jobJackets",
	PAY_PLANS: "payPlans",
	FINANCE_BUILDIUM_SYNC: "financeBuildiumSync",
	TENANT_CONTACT_SYNC: "tenantContactSync",
	PODCAST_EPISODES: "podcastEpisodes",
	TRANSACTION_EVENTS: "transactionEvents",
	FINANCIAL_REVIEW: "financial",
	FILE_GROUPS: "fileGroups"
} as const;

export function validatePermissionCatalog(
	catalog: Readonly<Record<string, Readonly<Record<string, PermissionConstantDTO>>>> = PERMISSIONS
): ReadonlyMap<string, PermissionConstantDTO> {
	const index = new Map<string, PermissionConstantDTO>();
	for (const grants of Object.values(catalog)) {
		for (const grant of Object.values(grants)) {
			const existing = index.get(grant.name);
			if (existing && !samePermission(existing, grant)) {
				throw new Error(`Conflicting permission catalog tuple: ${grant.name}`);
			}
			if (grant.name !== `${grant.domain}.${grant.action}.${grant.scope}`) {
				throw new Error(`Invalid permission catalog tuple: ${grant.name}`);
			}
			index.set(grant.name, grant);
		}
	}
	return index;
}

function samePermission(a: PermissionConstantDTO, b: PermissionConstantDTO): boolean {
	return (
		a.name === b.name && a.domain === b.domain && a.action === b.action && a.scope === b.scope
	);
}

export function validatePermission(
	permission: PermissionConstantDTO,
	catalog: ReadonlyMap<string, PermissionConstantDTO> = PERMISSION_CATALOG
): void {
	const declared = catalog.get(permission.name);
	if (!declared || !samePermission(declared, permission)) {
		throw new Error(`Uncataloged permission tuple: ${permission.name}`);
	}
}

function createPerm(domain: string, action: string, scope: string): PermissionConstantDTO {
	return {
		name: `${domain}.${action}.${scope}`,
		domain,
		action,
		scope
	};
}
export const FINANCIAL_VIEWER_PERMISSION: PermissionConstantDTO = createPerm(
	DOMAINS.FINANCIAL_REVIEW,
	"viewer",
	"all"
);

export const FINANCIAL_MODERATOR_PERMISSION: PermissionConstantDTO = createPerm(
	DOMAINS.FINANCIAL_REVIEW,
	"moderator",
	"all"
);

export const PERMISSIONS = {
	acquisitionReferral: {
		createAll: createPerm("acquisitionReferral", "create", "all"),
		readOwn: createPerm("acquisitionReferral", "read", "own"),
		readAll: createPerm("acquisitionReferral", "read", "all"),
		updateOwn: createPerm("acquisitionReferral", "update", "own"),
		updateAll: createPerm("acquisitionReferral", "update", "all"),
		deleteOwn: createPerm("acquisitionReferral", "delete", "own"),
		deleteAll: createPerm("acquisitionReferral", "delete", "all")
	},
	teamCallins: {
		createAll: createPerm("teamCallins", "create", "all"),
		readOwn: createPerm("teamCallins", "read", "own"),
		readAll: createPerm("teamCallins", "read", "all"),
		updateAll: createPerm("teamCallins", "update", "all"),
		deleteAll: createPerm("teamCallins", "delete", "all")
	},
	qrCodes: {
		createAll: createPerm("qrCodes", "create", "all"),
		readAll: createPerm("qrCodes", "read", "all"),
		updateAll: createPerm("qrCodes", "update", "all"),
		deleteAll: createPerm("qrCodes", "delete", "all")
	},
	galleries: {
		createAll: createPerm("galleries", "create", "all"),
		readAll: createPerm("galleries", "read", "all"),
		updateAll: createPerm("galleries", "update", "all"),
		deleteAll: createPerm("galleries", "delete", "all")
	},
	galleryFolders: {
		createAll: createPerm("galleryFolders", "create", "all"),
		readAll: createPerm("galleryFolders", "read", "all"),
		updateAll: createPerm("galleryFolders", "update", "all"),
		deleteAll: createPerm("galleryFolders", "delete", "all")
	},
	system: {
		createAll: createPerm("system", "create", "all"),
		readAll: createPerm("system", "read", "all"),
		updateAll: createPerm("system", "update", "all"),
		deleteAll: createPerm("system", "delete", "all")
	},
	notes: {
		createAll: createPerm("notes", "create", "all"),
		readAll: createPerm("notes", "read", "all"),
		updateAll: createPerm("notes", "update", "all"),
		deleteAll: createPerm("notes", "delete", "all")
	},
	debt: {
		createAll: createPerm("debt", "create", "all"),
		readAll: createPerm("debt", "read", "all"),
		updateAll: createPerm("debt", "update", "all"),
		deleteAll: createPerm("debt", "delete", "all")
	},
	parties: {
		createAll: createPerm("parties", "create", "all"),
		readAll: createPerm("parties", "read", "all"),
		updateAll: createPerm("parties", "update", "all"),
		deleteAll: createPerm("parties", "delete", "all")
	},
	companies: {
		createAll: createPerm("companies", "create", "all"),
		readAll: createPerm("companies", "read", "all"),
		updateAll: createPerm("companies", "update", "all"),
		deleteAll: createPerm("companies", "delete", "all")
	},
	companyStructure: {
		readAll: createPerm("companyStructure", "read", "all"),
		manageAll: createPerm("companyStructure", "manage", "all")
	},
	contacts: {
		createAll: createPerm("contacts", "create", "all"),
		readAll: createPerm("contacts", "read", "all"),
		updateAll: createPerm("contacts", "update", "all"),
		deleteAll: createPerm("contacts", "delete", "all")
	},
	realEstateAssets: {
		createAll: createPerm("realEstateAssets", "create", "all"),
		readAll: createPerm("realEstateAssets", "read", "all"),
		updateAll: createPerm("realEstateAssets", "update", "all"),
		deleteAll: createPerm("realEstateAssets", "delete", "all")
	},
	addresses: {
		createAll: createPerm("addresses", "create", "all"),
		readAll: createPerm("addresses", "read", "all"),
		updateAll: createPerm("addresses", "update", "all"),
		deleteAll: createPerm("addresses", "delete", "all")
	},
	investments: {
		createAll: createPerm("investments", "create", "all"),
		readAll: createPerm("investments", "read", "all"),
		updateAll: createPerm("investments", "update", "all"),
		deleteAll: createPerm("investments", "delete", "all")
	},
	offerings: {
		createAll: createPerm("offerings", "create", "all"),
		readAll: createPerm("offerings", "read", "all"),
		updateAll: createPerm("offerings", "update", "all"),
		deleteAll: createPerm("offerings", "delete", "all")
	},
	permitReports: {
		createAll: createPerm("permitReports", "create", "all"),
		readAll: createPerm("permitReports", "read", "all"),
		updateAll: createPerm("permitReports", "update", "all"),
		deleteAll: createPerm("permitReports", "delete", "all")
	},
	cadParcels: {
		createAll: createPerm("cadParcels", "create", "all"),
		readAll: createPerm("cadParcels", "read", "all"),
		updateAll: createPerm("cadParcels", "update", "all"),
		deleteAll: createPerm("cadParcels", "delete", "all")
	},
	capex: {
		createAll: createPerm("capex", "create", "all"),
		readAll: createPerm("capex", "read", "all"),
		updateAll: createPerm("capex", "update", "all"),
		deleteAll: createPerm("capex", "delete", "all")
	},
	investmentListings: {
		createAll: createPerm("investmentListings", "create", "all"),
		readAll: createPerm("investmentListings", "read", "all"),
		updateAll: createPerm("investmentListings", "update", "all"),
		deleteAll: createPerm("investmentListings", "delete", "all")
	},
	teamMembers: {
		createAll: createPerm("teamMembers", "create", "all"),
		readAll: createPerm("teamMembers", "read", "all"),
		readSensitiveAll: createPerm("teamMembers", "readSensitive", "all"),
		updateAll: createPerm("teamMembers", "update", "all"),
		updateSensitiveAll: createPerm("teamMembers", "updateSensitive", "all"),
		deleteAll: createPerm("teamMembers", "delete", "all")
	},
	pendingTeamMembers: {
		createAll: createPerm(DOMAINS.PENDING_TEAM_MEMBERS, "create", "all"),
		readAll: createPerm(DOMAINS.PENDING_TEAM_MEMBERS, "read", "all"),
		updateAll: createPerm(DOMAINS.PENDING_TEAM_MEMBERS, "update", "all"),
		deleteAll: createPerm(DOMAINS.PENDING_TEAM_MEMBERS, "delete", "all")
	},
	devices: {
		createAll: createPerm("devices", "create", "all"),
		readAll: createPerm("devices", "read", "all"),
		updateAll: createPerm("devices", "update", "all"),
		deleteAll: createPerm("devices", "delete", "all")
	},
	jobJackets: {
		createAll: createPerm(DOMAINS.JOB_JACKETS, "create", "all"),
		readAll: createPerm(DOMAINS.JOB_JACKETS, "read", "all"),
		updateAll: createPerm(DOMAINS.JOB_JACKETS, "update", "all")
	},
	payPlans: {
		createAll: createPerm(DOMAINS.PAY_PLANS, "create", "all"),
		readAll: createPerm(DOMAINS.PAY_PLANS, "read", "all"),
		updateAll: createPerm(DOMAINS.PAY_PLANS, "update", "all"),
		deleteAll: createPerm(DOMAINS.PAY_PLANS, "delete", "all")
	},
	teamCharts: {
		createAll: createPerm("teamCharts", "create", "all"),
		readAll: createPerm("teamCharts", "read", "all"),
		readDrafts: createPerm("teamCharts", "readDrafts", "all"),
		updateAll: createPerm("teamCharts", "update", "all"),
		deleteAll: createPerm("teamCharts", "delete", "all"),
		promoteAll: createPerm("teamCharts", "promote", "all")
	},
	mlsSupraEvents: {
		createAll: createPerm("mlsSupraEvents", "create", "all"),
		readAll: createPerm("mlsSupraEvents", "read", "all"),
		updateAll: createPerm("mlsSupraEvents", "update", "all"),
		deleteAll: createPerm("mlsSupraEvents", "delete", "all")
	},
	teamShoutouts: {
		createAll: createPerm("teamShoutouts", "create", "all"),
		readAll: createPerm("teamShoutouts", "read", "all"),
		updateAll: createPerm("teamShoutouts", "update", "all"),
		deleteAll: createPerm("teamShoutouts", "delete", "all")
	},
	financeBuildiumSync: {
		updateAll: createPerm("financeBuildiumSync", "update", "all")
	},
	tenantContactSync: {
		readAll: createPerm("tenantContactSync", "read", "all"),
		runAll: createPerm("tenantContactSync", "run", "all")
	},
	podcastEpisodes: {
		createAll: createPerm("podcastEpisodes", "create", "all"),
		readAll: createPerm("podcastEpisodes", "read", "all"),
		updateAll: createPerm("podcastEpisodes", "update", "all"),
		deleteAll: createPerm("podcastEpisodes", "delete", "all")
	},
	transactionEvents: {
		readAll: createPerm("transactionEvents", "read", "all"),
		createAll: createPerm("transactionEvents", "create", "all"),
		updateAll: createPerm("transactionEvents", "update", "all"),
		deleteAll: createPerm("transactionEvents", "delete", "all")
	},
	financialReview: {
		viewer: FINANCIAL_VIEWER_PERMISSION,
		moderator: FINANCIAL_MODERATOR_PERMISSION
	},
	fileGroups: {
		readSensitiveAll: createPerm("fileGroups", "readSensitive", "all")
	}
} as const;

const PERMISSION_CATALOG = validatePermissionCatalog();
